/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiMediaInputSettingsConfig} from "../generated/UiMediaInputSettingsConfig";
import {UiAudioActivityDisplay} from "./micro-components/UiAudioActivityDisplay";
import {UiWebRtcPublisherConfig} from "../generated/UiWebRtcPublisherConfig";
import {UiSpinner} from "./micro-components/UiSpinner";
import {UiComponent} from "./UiComponent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {applyDisplayMode} from "./Common";
import {UiPageDisplayMode} from "../generated/UiPageDisplayMode";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";

type WebRtcState = 'new' | 'checking' | 'connected' | 'completed' | 'failed' | 'disconnected' | 'closed';

interface SdpDescriptorEnhanceData {
	audioBitrate: number,
	videoBitrate: number,
	videoFrameRate: number
}

export class UiWebRtcPublisher extends UiComponent<UiWebRtcPublisherConfig> {

	private static readonly PEER_CONNECTION_CONFIG: any = {'iceServers': []};

	private $main: JQuery;
	private $videoContainer: JQuery;
	private video: HTMLVideoElement;
	private $audioActivityDisplayContainer: JQuery;
	private $spinnerContainer: JQuery;

	private publishSettings: { webSocketUrl: string, wowzaApplicationName: string, streamName: string, inputSettings: UiMediaInputSettingsConfig, showLocalVideo: boolean };
	private publishingSignalingWsConnection: WebSocket = null;
	private publishingPeerConnection: RTCPeerConnection = null;
	private localStream: MediaStream = null;
	private showingLocalVideo: boolean;
	private publishingUserData: { param1: string };
	private publishingIceConnectionState: WebRtcState;

	private playingSettings: { websocketUrl: string, wowzaApplicationName: string, streamName: string };
	private playingSignalingWsConnection: WebSocket = null;
	private playingPeerConnection: RTCPeerConnection = null;
	private playingUserData: object;
	private playingStreamInfo: { applicationName: string; streamName: string; sessionId: string };
	private playingIceConnectionState: WebRtcState;

	private audioActivityDisplay: UiAudioActivityDisplay;

	constructor(config: UiWebRtcPublisherConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$main = $(`
<div class="UiWebRtcPublisher">
  <div class="video-container">
    <video autoplay></video>
    <div class="audio-activity-display-container hidden"/>
	<div class="spinner-container hidden"></div>	
  </div> 
</div>`);
		this.video = <HTMLVideoElement> this.$main.find('video')[0];
		this.video.onloadedmetadata = this.onResize.bind(this);
		this.$videoContainer = this.$main.find('.video-container');
		this.$audioActivityDisplayContainer = this.$main.find('.audio-activity-display-container');
		this.audioActivityDisplay = new UiAudioActivityDisplay();
		this.$audioActivityDisplayContainer.append(this.audioActivityDisplay.getMainDomElement());
		this.$spinnerContainer = this.$main.find('.spinner-container');
		this.$spinnerContainer.append(new UiSpinner().getMainDomElement());
	}

	public publish(webSocketUrl: string, wowzaApplicationName: string, streamName: string, inputSettings: UiMediaInputSettingsConfig, showLocalVideo: boolean) {
		this.unPublish();
		this.publishSettings = {webSocketUrl, wowzaApplicationName, streamName, inputSettings, showLocalVideo};
		let onUserMediaSuccess = (userMediaStream: MediaStream) => {
			this.logger.debug("getUserMediaSuccess: " + userMediaStream);
			this.localStream = userMediaStream;
			this.audioActivityDisplay.bindToStream(userMediaStream);
			this.showingLocalVideo = showLocalVideo;
			if (showLocalVideo) {
				this.video.muted = true;
				this.video.src = window.URL.createObjectURL(userMediaStream);
			}

			this.reconnectForPublishing();
		};

		let constraints = {
			video: inputSettings.video ? {
				width: inputSettings.videoWidth,
				height: inputSettings.videoHeight
			} : false,
			audio: true
		};
		if (navigator.mediaDevices.getUserMedia) {
			navigator.mediaDevices.getUserMedia(constraints).then(onUserMediaSuccess).catch(this.errorHandler.bind(this));
		} else if (navigator.getUserMedia) {
			navigator.getUserMedia(constraints, onUserMediaSuccess, this.errorHandler.bind(this));
		}
	}

	private reconnectForPublishing() {
		if (!this.publishSettings) {
			return;
		}

		this.publishingSignalingWsConnection = new WebSocket(this.publishSettings.webSocketUrl);
		this.publishingSignalingWsConnection.binaryType = 'arraybuffer';
		this.publishingSignalingWsConnection.onopen = () => {
			this.logger.debug("this.playingSignalingWsConnection.onopen");
			this.publishingPeerConnection = new RTCPeerConnection(UiWebRtcPublisher.PEER_CONNECTION_CONFIG);
			this.publishingPeerConnection.onicecandidate = this.gotIceCandidate.bind(this);
			this.publishingPeerConnection.oniceconnectionstatechange = this.onPublishingIceConnectionStateChange.bind(this);
			this.publishingPeerConnection.addStream(this.localStream);
			this.publishingPeerConnection.createOffer()
				.then(sdpDescriptionInit => {
					this.gotDescriptionForPublishing(this.publishSettings.wowzaApplicationName, this.publishSettings.streamName, this.publishSettings.inputSettings, sdpDescriptionInit)
				}, this.errorHandler.bind(this));
		};
		this.publishingSignalingWsConnection.onmessage = (evt) => {
			this.logger.debug("this.playingSignalingWsConnection.onmessage: " + evt.data);
			const msgJSON = JSON.parse(evt.data);
			const msgStatus = Number(msgJSON['status']);
			this.logger.info(`Status: ${msgStatus} - ${msgJSON['statusDescription']}`);
			if (msgStatus == 200) {
				if (msgJSON.sdp !== undefined) {
					this.logger.debug('sdp: ' + JSON.stringify(msgJSON['sdp']));
					this.publishingPeerConnection.setRemoteDescription(new RTCSessionDescription(msgJSON.sdp))
						.then(() => {
							//peerConnection.createAnswer(gotDescription, errorHandler);
						}, this.errorHandler.bind(this));
				}
				const iceCandidates = msgJSON['iceCandidates'];
				if (iceCandidates !== undefined) {
					for (let index in iceCandidates) {
						this.logger.debug('iceCandidates: ' + JSON.stringify(iceCandidates[index]));
						this.publishingPeerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]));
					}
				}
			} else {
				this.logger.error(msgJSON.statusDescription);
				setTimeout(() => this.reconnectForPublishing(), 1000);
			}
			if (this.publishingSignalingWsConnection != null) {
				this.publishingSignalingWsConnection.close();
			}
			this.publishingSignalingWsConnection = null;
		};
		this.publishingSignalingWsConnection.onclose = () => {
			this.logger.debug("this.playingSignalingWsConnection.onclose");
		};
		this.publishingSignalingWsConnection.onerror = (evt) => {
			this.logger.error('WebSocket connection failed: ' + this.publishSettings.webSocketUrl);
			this.logger.debug("this.playingSignalingWsConnection.onerror: " + JSON.stringify(evt));
			this.playingSignalingWsConnection = null;
			setTimeout(() => this.reconnectForPublishing(), 1000);
		};
		this.updateUi();
	}

	private unPublish() {
		this.publishSettings = null;
		if (this.publishingPeerConnection != null) {
			this.publishingPeerConnection.close();
		}
		this.publishingPeerConnection = null;

		if (this.publishingSignalingWsConnection != null) {
			this.publishingSignalingWsConnection.close();
		}
		this.publishingSignalingWsConnection = null;
		if (this.showingLocalVideo) {
			this.video.src = "";
			this.video.muted = false;
			this.showingLocalVideo = false;
		}
		if (this.localStream) {
			this.localStream.getTracks().forEach(track => track.stop());
			this.localStream = null;
		}
	}

	private play(websocketUrl: string, wowzaApplicationName: string, streamName: string) {
		this.stopPlaying();
		this.playingSettings = {websocketUrl, wowzaApplicationName, streamName};
		this.reconnectForPlaying();
	}

	private reconnectForPlaying() {
		if (!this.playingSettings) {
			return;
		}
		this.playingStreamInfo = {
			applicationName: this.playingSettings.wowzaApplicationName,
			streamName: this.playingSettings.streamName,
			sessionId: "[empty]"
		};

		this.playingSignalingWsConnection = new WebSocket(this.playingSettings.websocketUrl);
		this.playingSignalingWsConnection.binaryType = 'arraybuffer';
		this.playingSignalingWsConnection.onopen = () => {
			this.logger.debug("this.playingSignalingWsConnection.onopen");
			this.playingPeerConnection = new RTCPeerConnection(UiWebRtcPublisher.PEER_CONNECTION_CONFIG);
			this.playingPeerConnection.onicecandidate = this.gotIceCandidate.bind(this);
			this.playingPeerConnection.oniceconnectionstatechange = this.onPlayingIceConnectionStateChange.bind(this);
			this.playingPeerConnection.onaddstream = this.gotRemoteStream.bind(this);
			(this.playingPeerConnection as any).ontrack = this.gotRemoteTrack.bind(this);
			this.logger.debug("wsURL: " + this.playingSettings.websocketUrl);
			this.sendPlayGetOffer(this.playingSettings.wowzaApplicationName, this.playingSettings.streamName);
		};
		this.playingSignalingWsConnection.onmessage = (evt) => {
			this.logger.debug("this.playingSignalingWsConnection.onmessage: " + evt.data);
			const msgJSON = JSON.parse(evt.data);
			const msgStatus = Number(msgJSON['status']);

			if (msgStatus == 514) {  // repeater stream not ready
				setTimeout(() => this.sendPlayGetOffer(this.playingSettings.wowzaApplicationName, this.playingSettings.streamName), 1000);
			} else if (msgStatus == 200) {
				if (msgJSON.streamInfo !== undefined) {
					this.playingStreamInfo.sessionId = msgJSON.streamInfo.sessionId;
				}

				if (msgJSON.sdp !== undefined) {
					this.logger.debug('sdp: ' + JSON.stringify(msgJSON.sdp));
					this.playingPeerConnection.setRemoteDescription(new RTCSessionDescription(msgJSON.sdp))
						.then(() => {
							this.playingPeerConnection.createAnswer()
								.then(this.gotDescriptionForPlaying.bind(this), this.errorHandler.bind(this));
						}, this.errorHandler.bind(this));
				}
				const iceCandidates = msgJSON['iceCandidates'];
				if (iceCandidates !== undefined) {
					for (let index in iceCandidates) {
						this.logger.debug('iceCandidates: ' + JSON.stringify(iceCandidates[index]));
						this.playingPeerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]));
					}
				}
			} else {
				this.logger.error(msgJSON.statusDescription);
				setTimeout(() => this.sendPlayGetOffer(this.playingSettings.wowzaApplicationName, this.playingSettings.streamName), 1000);
			}

			if ('sendResponse'.localeCompare(msgJSON.command) == 0 && this.playingSignalingWsConnection != null) {
				this.playingSignalingWsConnection.close();
				this.playingSignalingWsConnection = null;
			}
		};

		this.playingSignalingWsConnection.onclose = () => {
			this.logger.debug("this.playingSignalingWsConnection.onclose");
			this.playingSignalingWsConnection = null;
		};

		this.playingSignalingWsConnection.onerror = (evt) => {
			this.logger.debug("this.playingSignalingWsConnection.onerror: " + JSON.stringify(evt));
			this.logger.error('WebSocket connection failed: ' + this.playingSettings.websocketUrl);
			this.playingSignalingWsConnection = null;
			setTimeout(() => this.reconnectForPlaying(), 1000)
		};
		this.updateUi();
	}

	private stopPlaying() {
		this.playingSettings = null;
		if (this.playingPeerConnection != null) {
			this.playingPeerConnection.close();
		}
		this.playingPeerConnection = null;

		if (this.playingSignalingWsConnection != null) {
			this.playingSignalingWsConnection.close();
		}
		this.playingSignalingWsConnection = null;

		this.video.src = ""; // this seems like a chrome bug - if set to null it will make HTTP request

		this.logger.debug("stopPlay");
	}

	private sendPlayGetOffer(wowzaApplicationName: string, streamName: string) {
		this.playingUserData = {param1: "value1"}; // TODO delete or make use of...
		this.logger.debug("sendPlayGetOffer: " + JSON.stringify(this.playingStreamInfo));
		this.playingSignalingWsConnection.send('{"direction":"play", "command":"getOffer", "streamInfo":' + JSON.stringify(this.playingStreamInfo) + ', "userData":' + JSON.stringify(this.playingUserData) + '}');
	}

	private gotIceCandidate(event: RTCPeerConnectionIceEvent) {
		if (event.candidate != null) {
			this.logger.debug('gotIceCandidate: ' + JSON.stringify({'ice': event.candidate}));
		}
	}

	private onPublishingIceConnectionStateChange(event: Event) {
		let peerConnection = event.currentTarget as RTCPeerConnection;
		let state = peerConnection.iceConnectionState;
		this.publishingIceConnectionState = state;
		this.logger.debug("publishingIceConnectionState: " + state);

		if (state === "closed") {
			this.audioActivityDisplay.unbind();
		}
		if (state === "failed") {
			setTimeout(() => this.reconnectForPublishing(), 1000);
		}

		this.updateUi();
	}

	private onPlayingIceConnectionStateChange(event: Event) {
		let peerConnection = event.currentTarget as RTCPeerConnection;
		let state = peerConnection.iceConnectionState;
		this.playingIceConnectionState = state;
		this.logger.debug("playingIceConnectionState: " + state);

		if (state === "failed") {
			setTimeout(() => this.reconnectForPlaying(), 1000);
		}

		this.updateUi();
	}

	private updateUi() {
		const nonLoadingIceStates: WebRtcState[] = ['connected', 'completed', 'closed'];
		let loading: boolean = (this.playingSignalingWsConnection != null || nonLoadingIceStates.indexOf(this.playingIceConnectionState) === -1)
			&& (this.publishingSignalingWsConnection != null || nonLoadingIceStates.indexOf(this.publishingIceConnectionState) === -1);

		this.$spinnerContainer.toggleClass("hidden", !loading);
		this.$audioActivityDisplayContainer.toggleClass("hidden", this.publishingIceConnectionState !== "completed");
	}

	private gotDescriptionForPublishing(wowzaApplicationName: string, streamName: string, inputSettings: UiMediaInputSettingsConfig, description: RTCSessionDescriptionInit) {
		const enhanceData: SdpDescriptorEnhanceData = {
			audioBitrate: Number(inputSettings.audioKiloBitsPerSecond),
			videoBitrate: Number(inputSettings.videoKiloBitsPerSecond),
			videoFrameRate: Number(inputSettings.videoFps)
		};

		description.sdp = this.enhanceSDP(description.sdp, enhanceData);

		this.logger.debug('gotDescription: ' + JSON.stringify({'sdp': description}));

		this.publishingPeerConnection.setLocalDescription(description)
			.then(() => {
				let streamInfo = {
					applicationName: wowzaApplicationName,
					streamName: streamName,
					sessionId: "[empty]"
				};
				this.publishingUserData = {param1: "value1"};
				this.publishingSignalingWsConnection.send('{"direction":"publish", "command":"sendOffer", "streamInfo":' + JSON.stringify(streamInfo) + ', "sdp":' + JSON.stringify(description) + ', "userData":' + JSON.stringify(this.publishingUserData) + '}');
			}, () => {
				this.logger.debug('set description error')
			});
	}

	private enhanceSDP(sdpStr: string, enhanceData: SdpDescriptorEnhanceData) {
		const sdpLines = sdpStr.split(/\r\n/);
		let sdpSection = 'header';
		let hitMID = false;
		let sdpStrRet = '';

		for (let sdpIndex in sdpLines) {
			const sdpLine = sdpLines[sdpIndex];

			if (sdpLine.length <= 0)
				continue;

			sdpStrRet += sdpLine;

			if (sdpLine.indexOf("m=audio") === 0) {
				sdpSection = 'audio';
				hitMID = false;
			} else if (sdpLine.indexOf("m=video") === 0) {
				sdpSection = 'video';
				hitMID = false;
			} else if (sdpLine.indexOf("a=rtpmap") == 0) {
				sdpSection = 'bandwidth';
				hitMID = false;
			}

			if (sdpLine.indexOf("a=mid:") === 0 || sdpLine.indexOf("a=rtpmap") == 0) {
				if (!hitMID) {
					if ('audio'.localeCompare(sdpSection) == 0) {
						if (enhanceData.audioBitrate !== undefined) {
							sdpStrRet += '\r\nb=CT:' + (enhanceData.audioBitrate);
							sdpStrRet += '\r\nb=AS:' + (enhanceData.audioBitrate);
						}
						hitMID = true;
					} else if ('video'.localeCompare(sdpSection) == 0) {
						if (enhanceData.videoBitrate !== undefined) {
							sdpStrRet += '\r\nb=CT:' + (enhanceData.videoBitrate);
							sdpStrRet += '\r\nb=AS:' + (enhanceData.videoBitrate);
							if (enhanceData.videoFrameRate !== undefined) {
								sdpStrRet += '\r\na=framerate:' + enhanceData.videoFrameRate;
							}
						}
						hitMID = true;
					} else if ('bandwidth'.localeCompare(sdpSection) == 0) {
						let rtpmapID;
						rtpmapID = this.getRtpMapID(sdpLine);
						if (rtpmapID !== null) {
							const match = rtpmapID[2].toLowerCase();
							if (('vp9'.localeCompare(match) == 0) || ('vp8'.localeCompare(match) == 0) || ('h264'.localeCompare(match) == 0) ||
								('red'.localeCompare(match) == 0) || ('ulpfec'.localeCompare(match) == 0) || ('rtx'.localeCompare(match) == 0)) {
								if (enhanceData.videoBitrate !== undefined) {
									sdpStrRet += '\r\na=fmtp:' + rtpmapID[1] + ' x-google-min-bitrate=' + (enhanceData.videoBitrate) + ';x-google-max-bitrate=' + (enhanceData.videoBitrate);
								}
							}

							if (('opus'.localeCompare(match) == 0) || ('isac'.localeCompare(match) == 0) || ('g722'.localeCompare(match) == 0) || ('pcmu'.localeCompare(match) == 0) ||
								('pcma'.localeCompare(match) == 0) || ('cn'.localeCompare(match) == 0)) {
								if (enhanceData.audioBitrate !== undefined) {
									sdpStrRet += '\r\na=fmtp:' + rtpmapID[1] + ' x-google-min-bitrate=' + (enhanceData.audioBitrate) + ';x-google-max-bitrate=' + (enhanceData.audioBitrate);
								}
							}
						}
					}
				}
			}
			sdpStrRet += '\r\n';
		}
		return sdpStrRet;
	}

	private getRtpMapID(line: string) {
		const findid = new RegExp('a=rtpmap:(\\d+) (\\w+)/(\\d+)');
		const found = line.match(findid);
		return (found && found.length >= 3) ? found : null;
	}

	private gotDescriptionForPlaying(description: RTCSessionDescription) {
		this.logger.debug('gotDescription');
		this.playingPeerConnection.setLocalDescription(description)
			.then(() => {
				this.logger.debug('sendAnswer');

				this.playingSignalingWsConnection.send('{"direction":"play", "command":"sendResponse", "streamInfo":' + JSON.stringify(this.playingStreamInfo) + ', "sdp":' + JSON.stringify(description) + ', "userData":' + JSON.stringify(this.playingUserData) + '}');

			}, () => {
				this.logger.debug('set description error')
			});
	}

	private gotRemoteTrack(event: any) {
		this.logger.debug('gotRemoteTrack: kind:' + event.track.kind + ' stream:' + event.streams[0]);
		if (!this.showingLocalVideo) { // TODO handle receiving audio while showing local video
			this.video.muted = false;
			this.video.src = window.URL.createObjectURL(event.streams[0]);
		}
	}

	private gotRemoteStream(event: MediaStreamEvent) {
		this.logger.debug('gotRemoteStream: ' + event.stream);
		if (!this.showingLocalVideo) { // TODO handle receiving audio while showing local video
			this.video.muted = false;
			this.video.src = window.URL.createObjectURL(event.stream);
		}
	}

	private errorHandler(error: any) {
		this.logger.error(error);
	}

	public onResize(): void {
		applyDisplayMode(this.$main, this.$videoContainer, UiPageDisplayMode.FIT_SIZE, {
			innerPreferedDimensions: {
				width: this.video.videoWidth,
				height: this.video.videoHeight
			}
		});
	}

	destroy(): void {
	}

	getMainDomElement(): JQuery {
		return this.$main;
	}

}

navigator.getUserMedia = navigator.getUserMedia || (navigator as any).mozGetUserMedia || (navigator as any).webkitGetUserMedia;
(window as any).RTCPeerConnection = (window as any).RTCPeerConnection || (window as any).mozRTCPeerConnection || (window as any).webkitRTCPeerConnection;
(window as any).RTCIceCandidate = (window as any).RTCIceCandidate || (window as any).mozRTCIceCandidate || (window as any).webkitRTCIceCandidate;
(window as any).RTCSessionDescription = (window as any).RTCSessionDescription || (window as any).mozRTCSessionDescription || (window as any).webkitRTCSessionDescription;

TeamAppsUiComponentRegistry.registerComponentClass("UiWebRtcPublisher", UiWebRtcPublisher);